import React, { useMemo, useState } from 'react'
import {
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button,  Typography, Switch, Backdrop, CircularProgress, Card, CardContent, Radio, RadioGroup, FormControlLabel, Checkbox, TextField, InputAdornment, IconButton, Box, Chip, ToggleButton, ToggleButtonGroup } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import PaymentForm from '../CreateAccount/PaymentForm'
import { DEALSAUCE_SUBSCRIBE } from './mutations'
import { GET_ME } from './queries'
import withSession from './withSession'

const DEALSAUCE_PRICES = gql`
  query dealsaucePrices($priceIds: [String!]!) {
    dealsaucePrices(priceIds: $priceIds) {
      id
      amount
      interval
      active
    }
  }
`

const DEALSAUCE_PAYMENT_METHODS = gql`
  query dealsaucePaymentMethods($orgId: ID) {
    dealsaucePaymentMethods(orgId: $orgId) {
      id
      brand
      last4
      expMonth
      expYear
      isDefault
    }
  }
`

const DEALSAUCE_PRORATION_PREVIEW = gql`
  query dealsauceProrationPreview(
    $priceId: String!
    $orgId: ID
    $coupon: String
  ) {
    dealsauceProrationPreview(
      priceId: $priceId
      orgId: $orgId
      coupon: $coupon
    ) {
      amountDue
      currency
      nextPaymentDate
      discountAmount
      subtotal
    }
  }
`

const DEALSAUCE_SET_DEFAULT_PM = gql`
  mutation dealsauceSetDefaultPaymentMethod(
    $orgId: ID
    $paymentMethodId: String!
  ) {
    dealsauceSetDefaultPaymentMethod(
      orgId: $orgId
      paymentMethodId: $paymentMethodId
    )
  }
`

const DEALSAUCE_DETACH_PM = gql`
  mutation dealsauceDetachPaymentMethod(
    $orgId: ID
    $paymentMethodId: String!
  ) {
    dealsauceDetachPaymentMethod(
      orgId: $orgId
      paymentMethodId: $paymentMethodId
    )
  }
`

const DEALSAUCE_ADD_PM = gql`
  mutation dealsauceAddPaymentMethod($orgId: ID, $stripeToken: JSON!) {
    dealsauceAddPaymentMethod(orgId: $orgId, stripeToken: $stripeToken)
  }
`

const DEALSAUCE_COUPON = gql`
  query dealsauceCoupon($couponId: String!) {
    dealsauceCoupon(couponId: $couponId) {
      id
      name
      valid
      percentOff
      amountOff
      currency
      duration
      durationInMonths
      metadata
    }
  }
`

// Regular (Affiliate) price IDs
const REGULAR_PRICE_IDS = {
  monthly: 'plan_RFRNZc45vs8Anz',
  annual: 'price_1ROjLGG0Nqc9OHilgy9wsVLL'}

// Hot price IDs (member-specific)
const HOT_PRICE_IDS = {
  pacemorby: {
    monthly: 'price_1SQbu2G0Nqc9OHilPHqLI7Pe',
    annual: 'price_1SQbtqG0Nqc9OHilOQd0RBSL'},
  astroflip: {
    monthly: 'price_1SQbxcG0Nqc9OHil9nollons',
    annual: 'price_1SQbxUG0Nqc9OHillLatgT12'}}

const stripePublicKey =
  import.meta.env.VITE_DEALSAUCE_STRIPE_PUBLIC_KEY ||
  import.meta.env.VITE_STRIPE_PUBLIC_KEY
const stripePromise = loadStripe(stripePublicKey)

const currencyWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0 })
const currencyCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2 })

const UnifiedSubscribeDialog = ({
  open,
  onClose,
  session,
  initialIsHot,
  initialYearly }) => {
  // PEP orgs should not use Dealsauce Stripe
  const member = session?.me?.activeOrg?.member
  if (member === 'pep') {
    if (onClose) onClose()
    return null
  }

  // Default to annual billing
  const [yearly, setYearly] = useState(true)
  // Default to regular plan
  const [isHot, setIsHot] = useState(false)
  const [stripeToken, setStripeToken] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // Default to using an existing card when available
  const [useExisting, setUseExisting] = useState(true)
  const [errorText, setErrorText] = useState('')
  const [selectedPm, setSelectedPm] = useState('')
  const [element, setElement] = useState(null)
  const [didInitPaymentChoice, setDidInitPaymentChoice] =
    useState(false)
  const [success, setSuccess] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  const [appliedCouponCode, setAppliedCouponCode] = useState('')
  const [appliedCouponData, setAppliedCouponData] = useState(null)
  const [couponError, setCouponError] = useState('')
  const [couponValidating, setCouponValidating] = useState(false)

  const orgId =
    session?.me?.organization ||
    session?.me?.activeOrg?._id ||
    session?.me?.activeOrgId ||
    null

  // Get member key for hot pricing (if applicable)
  const memberKey = session?.me?.activeOrg?.member
    ? String(session.me.activeOrg.member).toLowerCase()
    : null

  const regularPriceConfig = REGULAR_PRICE_IDS
  const hotPriceConfig = useMemo(
    () => HOT_PRICE_IDS[memberKey] || {},
    [memberKey],
  )

  const priceIds = useMemo(
    () =>
      [
        regularPriceConfig.monthly,
        regularPriceConfig.annual,
        hotPriceConfig.monthly,
        hotPriceConfig.annual,
      ].filter(Boolean),
    [hotPriceConfig],
  )

  const {
    data: pmData,
    refetch: refetchPms,
    loading: pmLoading} = useQuery(DEALSAUCE_PAYMENT_METHODS, {
    variables: orgId ? { orgId } : {},
    skip: !open,
    fetchPolicy: 'network-only'})
  const paymentMethods = pmData?.dealsaucePaymentMethods || []

  const { data } = useQuery(DEALSAUCE_PRICES, {
    variables: { priceIds },
    skip: !open || priceIds.length === 0,
    fetchPolicy: 'cache-first'})
  const byId = useMemo(() => {
    const map = {}
    ;(data?.dealsaucePrices || []).forEach((p) => (map[p.id] = p))
    return map
  }, [data])

  const regularMonthly =
    regularPriceConfig.monthly && byId[regularPriceConfig.monthly]
      ? byId[regularPriceConfig.monthly].amount
      : null
  const regularAnnual =
    regularPriceConfig.annual && byId[regularPriceConfig.annual]
      ? byId[regularPriceConfig.annual].amount
      : null
  const hotMonthly =
    hotPriceConfig.monthly && byId[hotPriceConfig.monthly]
      ? byId[hotPriceConfig.monthly].amount
      : null
  const hotAnnual =
    hotPriceConfig.annual && byId[hotPriceConfig.annual]
      ? byId[hotPriceConfig.annual].amount
      : null

  const selectedMonthly = isHot ? hotMonthly : regularMonthly
  const selectedAnnual = isHot ? hotAnnual : regularAnnual
  const display = yearly
    ? currencyWhole.format(Math.round((selectedAnnual || 0) / 1200))
    : currencyWhole.format(Math.round((selectedMonthly || 0) / 100))

  const selectedPriceId = isHot
    ? yearly
      ? hotPriceConfig.annual
      : hotPriceConfig.monthly
    : yearly
      ? regularPriceConfig.annual
      : regularPriceConfig.monthly

  const priceId = selectedPriceId
  const planTypeValue = isHot
    ? yearly
      ? 'annual - hot'
      : 'monthly - hot'
    : yearly
      ? 'annual'
      : 'monthly'

  const hasExistingCard = (paymentMethods || []).length > 0
  const orgSubs = session?.me?.activeOrg?.subscriptions || []
  const activeSub = Array.isArray(orgSubs)
    ? orgSubs.find(
        (s) =>
          s &&
          (s.status === 'active' ||
            s.status === 'trialing' ||
            s.status === 'past_due'),
      ) || orgSubs[0]
    : null

  const currentPlanInfo = React.useMemo(() => {
    if (!activeSub)
      return { hasSub: false, isHot: false, yearly: true }
    const rfId =
      activeSub?.realeflowPlanId ??
      activeSub?.plan?.realeflowPlanId ??
      null
    const planType = String(
      activeSub?.planType || activeSub?.planNickname || '',
    ).toLowerCase()
    const status = String(activeSub?.status || '').toLowerCase()
    const activeish =
      status === '' ||
      ['active', 'trialing', 'past_due'].includes(status)
    const isHotById = [1250, 1252, '1250', '1252'].includes(rfId)
    const isHotByType = planType.includes('hot')
    const isHotCurrent = activeish && (isHotById || isHotByType)
    const cps = Number(activeSub?.current_period_start || 0) || 0
    const cpe = Number(activeSub?.current_period_end || 0) || 0
    const secondsInDay = 60 * 60 * 24
    const periodDays =
      cps > 0 && cpe > 0 && cpe >= cps
        ? Math.round((cpe - cps) / secondsInDay)
        : null
    // If we don't have explicit text, infer cadence from period length.
    // Monthly is ~30 days; annual is ~365 days. Use a conservative threshold.
    const isYearlyFromDates =
      typeof periodDays === 'number' ? periodDays >= 200 : null
    const isYearlyFromText =
      planType.includes('annual') || planType.includes('year')
    const isMonthlyFromText = planType.includes('month')

    // Prefer dates (ground truth) when available; otherwise fall back to text.
    const isYearlyCurrent =
      isYearlyFromDates !== null
        ? isYearlyFromDates
        : isYearlyFromText
          ? true
          : isMonthlyFromText
            ? false
            : true
    return {
      hasSub: activeish,
      isHot: isHotCurrent,
      yearly: isYearlyCurrent}
  }, [activeSub])

  React.useEffect(() => {
    if (!open) return
    const nextIsHot =
      typeof initialIsHot === 'boolean'
        ? initialIsHot
        : currentPlanInfo.isHot
    const nextYearly =
      typeof initialYearly === 'boolean'
        ? initialYearly
        : currentPlanInfo.yearly
    setIsHot(nextIsHot)
    setYearly(nextYearly)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialIsHot, initialYearly])
  const currentPeriodEnd =
    (activeSub && activeSub.current_period_end) || null

  const selectedIsCurrentPlan = React.useMemo(() => {
    if (!currentPlanInfo.hasSub) return false
    return (
      Boolean(isHot) === Boolean(currentPlanInfo.isHot) &&
      Boolean(yearly) === Boolean(currentPlanInfo.yearly)
    )
  }, [currentPlanInfo, isHot, yearly])

  const currentPeriodStart =
    (activeSub && activeSub.current_period_start) || null
  const cancelAtPeriodEnd =
    (activeSub && activeSub.cancel_at_period_end) || false
  const subscriptionStatus = String(activeSub?.status || '')

  const formatDate = (sec) => {
    if (!sec) return '—'
    try {
      return new Date(Number(sec) * 1000).toLocaleDateString(
        'en-US',
        {
          month: 'long',
          day: 'numeric',
          year: 'numeric'},
      )
    } catch (e) {
      return '—'
    }
  }

  const isCardExpired = (pm) => {
    const month = Number(pm?.expMonth || 0) || 0
    const year = Number(pm?.expYear || 0) || 0
    if (!month || !year) return false
    const now = new Date()
    const curYear = now.getFullYear()
    const curMonth = now.getMonth() + 1
    if (year < curYear) return true
    if (year > curYear) return false
    return month < curMonth
  }

  const currentDefaultPm = React.useMemo(() => {
    if (!hasExistingCard) return null
    const def = paymentMethods.find((p) => p.isDefault)
    return def || paymentMethods[0] || null
  }, [hasExistingCard, paymentMethods])

  const selectedPmObj = React.useMemo(
    () => (paymentMethods || []).find((p) => p.id === selectedPm) || null,
    [paymentMethods, selectedPm],
  )
  const selectedPmExpired = React.useMemo(
    () => (selectedPmObj ? isCardExpired(selectedPmObj) : false),
    [selectedPmObj],
  )

  const defaultPmId = React.useMemo(() => {
    const def = (paymentMethods || []).find((p) => p.isDefault)
    return def?.id || ''
  }, [paymentMethods])
  // Check if subscription is currently in trial
  const isInTrial = React.useMemo(() => {
    if (!activeSub) return false
    return (
      activeSub.status === 'trialing' &&
      activeSub.trial_end &&
      activeSub.trial_end > Math.floor(Date.now() / 1000)
    )
  }, [activeSub])
  // For previewing with charge-now anchor, show proposed next payment
  const proposedNext = React.useMemo(() => {
    const d = new Date()
    const copy = new Date(d.getTime())
    if (yearly) {
      copy.setFullYear(copy.getFullYear() + 1)
    } else {
      copy.setMonth(copy.getMonth() + 1)
    }
    return copy
  }, [yearly])
  const nextPaymentDateText =
    proposedNext?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }) ||
    (currentPeriodEnd
      ? new Date(currentPeriodEnd * 1000).toLocaleDateString(
          'en-US',
          {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          },
        )
      : null)

  const currentLastPaymentText = formatDate(currentPeriodStart)
  const currentNextPaymentText = formatDate(currentPeriodEnd)
  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
    refetch: refetchPreview} = useQuery(DEALSAUCE_PRORATION_PREVIEW, {
    variables: {
      priceId,
      orgId,
      coupon: appliedCouponCode.trim() || null},
    skip: !open || !priceId,
    fetchPolicy: 'network-only'})

  React.useEffect(() => {
    if (previewError) {
      if (appliedCouponCode.trim()) {
        const errorMsg = previewError?.message || ''
        if (
          errorMsg.includes('coupon') ||
          errorMsg.includes('promo') ||
          errorMsg.includes('discount') ||
          errorMsg.includes('invalid')
        ) {
          setCouponError('Invalid coupon code')
        } else {
          setCouponError('')
        }
      }
      return
    }
    if (previewData) {
      setCouponError('')
    }
  }, [previewError, previewData, appliedCouponCode])

  const prorationAmountCents =
    previewData?.dealsauceProrationPreview?.amountDue ?? null
  const previewDiscountAmountCents =
    previewData?.dealsauceProrationPreview?.discountAmount ?? null
  const subtotalCents =
    previewData?.dealsauceProrationPreview?.subtotal ?? null

  // Base price in cents (before discounts)
  const basePriceCents = React.useMemo(() => {
    if (subtotalCents !== null) return subtotalCents || 0
    if (yearly && selectedAnnual) return selectedAnnual || 0
    if (!yearly && selectedMonthly) return selectedMonthly || 0
    return 0
  }, [subtotalCents, yearly, selectedAnnual, selectedMonthly])

  // Estimate the coupon discount off a base amount (used only when there is
  // no Stripe proration preview, e.g. a brand-new subscription).
  const estimatedCouponDiscountCents = React.useMemo(() => {
    if (!appliedCouponData || basePriceCents <= 0) return 0
    if (appliedCouponData.amountOff) {
      return Math.min(basePriceCents, appliedCouponData.amountOff)
    }
    if (appliedCouponData.percentOff) {
      return Math.round(
        basePriceCents * (Number(appliedCouponData.percentOff) / 100),
      )
    }
    return 0
  }, [appliedCouponData, basePriceCents])

  // Discount to display. For prorations the backend already computes it
  // against the prorated charge; otherwise fall back to the estimate.
  const discountAmountCents = React.useMemo(() => {
    if (prorationAmountCents !== null) {
      return previewDiscountAmountCents || 0
    }
    return estimatedCouponDiscountCents
  }, [
    prorationAmountCents,
    previewDiscountAmountCents,
    estimatedCouponDiscountCents,
  ])

  // Amount we display as "Amount due today".
  const amountDueDisplayCents = React.useMemo(() => {
    // Backend already nets the coupon out of the prorated amount.
    if (prorationAmountCents !== null) {
      return prorationAmountCents || 0
    }
    if (discountAmountCents > 0) {
      return Math.max(0, (basePriceCents || 0) - discountAmountCents)
    }
    return basePriceCents || 0
  }, [discountAmountCents, basePriceCents, prorationAmountCents])

  // Gross prorated charge before the coupon (net + discount), so the discount
  // line reconciles against an explicit prorated amount.
  const grossProratedCents = React.useMemo(() => {
    if (prorationAmountCents === null) return null
    return (prorationAmountCents || 0) + (discountAmountCents || 0)
  }, [prorationAmountCents, discountAmountCents])

  const [dealsauceSubscribe] = useMutation(DEALSAUCE_SUBSCRIBE, {
    refetchQueries: [{ query: GET_ME }],
  })
  const [setDefaultPm] = useMutation(DEALSAUCE_SET_DEFAULT_PM)
  const [detachPm] = useMutation(DEALSAUCE_DETACH_PM)
  const [addPm] = useMutation(DEALSAUCE_ADD_PM)
  const [getCoupon] = useLazyQuery(DEALSAUCE_COUPON, {
    fetchPolicy: 'network-only'})

  const [pmWorkingId, setPmWorkingId] = useState('')
  const [pmManageOpen, setPmManageOpen] = useState(false)
  const [addingCard, setAddingCard] = useState(false)
  const [addCardToken, setAddCardToken] = useState('')
  const [addCardElement, setAddCardElement] = useState(null)

  const createToken = async (stripe, cardElement, nameOnCard) => {
    if (!nameOnCard) setStripeToken('')
    else {
      const res = await stripe.createToken(cardElement, {
        name: nameOnCard,
      })
      if (res.error) setStripeToken('')
      else setStripeToken(res.token)
    }
  }

  const createAddCardToken = async (stripe, cardElement, nameOnCard) => {
    if (!nameOnCard) setAddCardToken('')
    else {
      const res = await stripe.createToken(cardElement, {
        name: nameOnCard,
      })
      if (res.error) setAddCardToken('')
      else setAddCardToken(res.token)
    }
  }

  React.useEffect(() => {
    if (useExisting && hasExistingCard && !selectedPm) {
      const def = paymentMethods.find((p) => p.isDefault)
      const firstValid =
        paymentMethods.find((p) => !isCardExpired(p)) || paymentMethods[0]
      const chosen = def || firstValid
      setSelectedPm(chosen ? chosen.id : paymentMethods[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useExisting, hasExistingCard, paymentMethods])

  const handleExistingToggle = () => {
    const next = !useExisting
    setUseExisting(next)
    setErrorText('')
    if (next) {
      refetchPms()
      if (hasExistingCard) {
        const def = paymentMethods.find((p) => p.isDefault)
        const firstValid =
          paymentMethods.find((p) => !isCardExpired(p)) || paymentMethods[0]
        const chosen = def || firstValid
        setSelectedPm(chosen ? chosen.id : paymentMethods[0].id)
      }
    } else {
      setSelectedPm('')
    }
  }

  // Initialize default payment selection once when dialog opens and data is available
  React.useEffect(() => {
    if (!open || didInitPaymentChoice || pmLoading) return
    if ((paymentMethods || []).length > 0) {
      setUseExisting(true)
      const def = paymentMethods.find((p) => p.isDefault)
      const firstValid =
        paymentMethods.find((p) => !isCardExpired(p)) || paymentMethods[0]
      const chosen = def || firstValid
      setSelectedPm(chosen ? chosen.id : paymentMethods[0].id)
    } else {
      setUseExisting(false)
      setSelectedPm('')
    }
    setDidInitPaymentChoice(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pmLoading, paymentMethods, didInitPaymentChoice])

  // Reset initializer when dialog closes
  React.useEffect(() => {
    if (!open) {
      setDidInitPaymentChoice(false)
      setCouponCode('')
      setAppliedCouponCode('')
      setAppliedCouponData(null)
      setCouponError('')
      setSuccess(false)
    }
  }, [open])

  // Refetch preview when plan type changes (yearly/monthly toggle or hot/regular toggle)
  React.useEffect(() => {
    if (open && priceId) {
      refetchPreview({
        priceId,
        orgId,
        coupon: appliedCouponCode.trim() || null,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearly, isHot, open, priceId])

  const handlePickPm = (pmId) => {
    setSelectedPm(pmId)
  }

  const handleMakeDefaultPm = async (pmId) => {
    setPmWorkingId(pmId)
    try {
      await setDefaultPm({
        variables: { orgId, paymentMethodId: pmId }})
      await refetchPms()
    } catch (e) {
    } finally {
      setPmWorkingId('')
    }
  }

  const handleRemovePm = async (pmId) => {
    const ok = window.confirm(
      'Remove this saved card? You can add it again later.',
    )
    if (!ok) return
    setPmWorkingId(pmId)
    try {
      await detachPm({ variables: { orgId, paymentMethodId: pmId } })
      await refetchPms()
      // If we removed the selected PM, choose a sane next selection
      if (selectedPm === pmId) {
        const remaining = (pmData?.dealsaucePaymentMethods || []).filter(
          (p) => p?.id && p.id !== pmId,
        )
        const next = remaining.find((p) => p.isDefault) || remaining[0]
        setSelectedPm(next?.id || '')
      }
    } catch (e) {
    } finally {
      setPmWorkingId('')
    }
  }

  const handleAddCard = async () => {
    if (!addCardToken) return
    setAddingCard(true)
    try {
      await addPm({
        variables: { orgId, stripeToken: addCardToken }})
      await refetchPms()
      setAddCardToken('')
      try {
        if (addCardElement?.clear) addCardElement.clear()
      } catch (e) {}
    } catch (e) {
    } finally {
      setAddingCard(false)
    }
  }

  const handleApplyCoupon = async () => {
    const codeToApply = couponCode.trim()
    if (!codeToApply) return

    setCouponError('')
    setCouponValidating(true)

    try {
      // First, query Stripe to get coupon details
      const { data: couponData } = await getCoupon({
        variables: { couponId: codeToApply }})

      const coupon = couponData?.dealsauceCoupon

      if (!coupon) {
        setCouponError('Invalid coupon code')
        setCouponValidating(false)
        return
      }

      if (!coupon.valid) {
        setCouponError('This coupon is no longer valid')
        setCouponValidating(false)
        return
      }

      // Check coupon metadata or name to determine plan type compatibility
      const metadata = coupon.metadata || {}
      const couponName = (coupon.name || '').toUpperCase()
      const couponId = (coupon.id || '').toUpperCase()

      // Check metadata for plan type
      const metadataPlanType = metadata.planType || metadata.plan_type
      const isForMonthly =
        metadataPlanType === 'monthly' ||
        couponName.includes('MONTH') ||
        couponId.includes('MONTH')
      const isForAnnual =
        metadataPlanType === 'annual' ||
        metadataPlanType === 'yearly' ||
        couponName.includes('YEAR') ||
        couponName.includes('ANNUAL') ||
        couponId.includes('YEAR') ||
        couponId.includes('ANNUAL')

      // Validate plan type compatibility
      if (isForMonthly && yearly) {
        setCouponError('This coupon is for monthly plans only')
        setCouponValidating(false)
        return
      }

      if (isForAnnual && !yearly) {
        setCouponError('This coupon is for annual plans only')
        setCouponValidating(false)
        return
      }

      // Coupon is valid, apply it
      setAppliedCouponCode(codeToApply)
      setAppliedCouponData(coupon)
      await refetchPreview({
        priceId,
        orgId,
        coupon: codeToApply,
      })
    } catch (error) {
      const errorMsg =
        error?.graphQLErrors?.[0]?.message || error?.message || ''
      if (
        errorMsg.includes('coupon') ||
        errorMsg.includes('promo') ||
        errorMsg.includes('discount') ||
        errorMsg.includes('invalid') ||
        errorMsg.includes('resource_missing')
      ) {
        setCouponError('Invalid coupon code')
      } else {
        setCouponError('Error validating coupon')
      }
    } finally {
      setCouponValidating(false)
    }
  }

  const handleClearCoupon = () => {
    setCouponCode('')
    setAppliedCouponCode('')
    setAppliedCouponData(null)
    setCouponError('')
    refetchPreview({ priceId, orgId, coupon: null  })
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setErrorText('')
    try {
      if (!priceId) {
        throw new Error(
          'Subscription pricing is unavailable for your account.',
        )
      }
      await dealsauceSubscribe({
        variables: {
          orgId: session?.me?.organization,
          email: session?.me?.email,
          priceId,
          stripeToken: useExisting ? null : stripeToken,
          coupon: appliedCouponCode.trim() || null,
          useExistingCard: useExisting,
          paymentMethodId: useExisting ? selectedPm || null : null,
          planType: planTypeValue}})
      setSubmitting(false)
      setSuccess(true)
    } catch (e) {
      setSubmitting(false)
      const msg =
        (e?.graphQLErrors && e.graphQLErrors[0]?.message) ||
        e?.message ||
        'Subscription failed.'
      const clean = String(msg)
        .replace(/^GraphQL error:\s*/i, '')
        .replace(/^Error:\s*/i, '')

      const lower = clean.toLowerCase()
      const friendly =
        lower.includes('card_declined') ||
        lower.includes('declined') ||
        lower.includes('insufficient') ||
        lower.includes('insufficient_funds')
          ? 'That card was declined. Try a different saved card or add a new one.'
          : lower.includes('expired')
            ? 'That card is expired. Please choose a different card or add a new one.'
            : null

      // Check if error is related to coupon
      if (
        clean.toLowerCase().includes('coupon') ||
        clean.toLowerCase().includes('promo') ||
        clean.toLowerCase().includes('discount') ||
        clean.toLowerCase().includes('invalid')
      ) {
        setCouponError('Invalid coupon code')
        setErrorText('')
      } else {
        setErrorText(friendly || clean)
      }
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      {submitting && (
        <Backdrop open={true} style={{ color: '#fff', zIndex: 2000 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <DialogTitle>{success ? 'All set' : 'Manage plan'}</DialogTitle>
      <DialogContent>
        {success ? (
          <Grid container spacing={2} sx={{
            alignItems: "center"
          }}>
            <Grid style={{ width: '100%' }}>
              <Typography variant="h5" align="center">
                Your plan has been updated.
              </Typography>
              <Typography align="center" style={{ marginTop: 8 }}>
                Changes take effect immediately.
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={3}>
              <Grid size={{xs: 12, md: 7}}>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#f8fafc',
                    border: '1px solid',
                    borderColor: 'divider'}}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'wrap'}}
                  >
                    <Box sx={{ minWidth: 260 }}>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 900, mb: 0.5, lineHeight: 1.15 }}
                      >
                        Compare plans
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          mb: currentPlanInfo.hasSub ? 1 : 0
                        }}>
                        Pick the plan that fits your workflow.
                      </Typography>
                      {currentPlanInfo.hasSub && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 800
                          }}>
                          Current plan:{' '}
                          <span
                            style={{ color: '#0b1020', fontWeight: 900 }}
                          >
                            {currentPlanInfo.yearly ? 'Annual' : 'Monthly'}{' '}
                            {currentPlanInfo.isHot ? 'Hot' : 'Regular'}
                          </span>
                        </Typography>
                      )}
                    </Box>

                    <ToggleButtonGroup
                      value={yearly ? 'annual' : 'monthly'}
                      exclusive
                      onChange={(_, next) => {
                        if (!next) return
                        const nextYearly = next === 'annual'
                        if (nextYearly === yearly) return
                        setYearly(nextYearly)
                        setAppliedCouponCode('')
                        setAppliedCouponData(null)
                        setCouponCode('')
                        setCouponError('')
                      }}
                      sx={{
                        borderRadius: 2,
                        bgcolor: '#f3f4f6',
                        p: 0.5,
                        '& .MuiToggleButton-root': {
                          border: 0,
                          borderRadius: 1.5,
                          px: 2,
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 900,
                          color: '#0b1020'},
                        '& .MuiToggleButton-root.Mui-selected': {
                          bgcolor: '#fff',
                          boxShadow:
                            '0 6px 18px rgba(15, 23, 42, 0.12)'}}}
                    >
                      <ToggleButton value="annual">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1}}
                        >
                          Annual
                          <Chip
                            size="small"
                            label="Save 20%"
                            color="primary"
                            sx={{ fontWeight: 900, height: 22 }}
                          />
                        </Box>
                      </ToggleButton>
                      <ToggleButton value="monthly">Monthly</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{xs: 12, sm: 6}}>
                    <Card
                      onClick={() => {
                        setIsHot(false)
                        setAppliedCouponCode('')
                        setAppliedCouponData(null)
                        setCouponCode('')
                        setCouponError('')
                      }}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: !isHot
                          ? 'primary.main'
                          : 'divider',
                        boxShadow: !isHot ? 6 : 1,
                        transition: 'all .15s ease',
                        '&:hover': {
                          boxShadow: 8,
                          transform: 'translateY(-1px)'}}}
                    >
                      <CardContent sx={{ pb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1}}
                        >
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 900,
                                lineHeight: 1.1}}
                            >
                              Regular
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary"
                              }}
                            >
                              Core Deal Sauce features
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: 900,
                              letterSpacing: -0.5}}
                          >
                            {yearly
                              ? currencyWhole.format(
                                  Math.round(
                                    (regularAnnual || 0) / 1200,
                                  ),
                                )
                              : currencyWhole.format(
                                  Math.round(
                                    (regularMonthly || 0) / 100,
                                  ),
                                )}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary"
                            }}
                          >
                            per user / month{' '}
                            {yearly
                              ? '· billed annually'
                              : '· billed monthly'}
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          {[
                            'Nationwide residential and commercial property search',
                            'Nationwide MLS access with agent data',
                            '10 cent skip tracing',
                            'Analyze off-market properties',
                            'Sold comp data',
                            '2 free team members ($75 value)',
                            'Much more...',
                          ].map((perk) => (
                            <Typography
                              key={perk}
                              variant="body2"
                              sx={{
                                display: 'flex',
                                gap: 1,
                                mb: 0.5}}
                            >
                              <span style={{ fontWeight: 900 }}>
                                ✓
                              </span>
                              <span>{perk}</span>
                            </Typography>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{xs: 12, sm: 6}}>
                    <Card
                      onClick={() => {
                        setIsHot(true)
                        setAppliedCouponCode('')
                        setAppliedCouponData(null)
                        setCouponCode('')
                        setCouponError('')
                      }}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: isHot
                          ? 'warning.main'
                          : 'divider',
                        boxShadow: isHot ? 8 : 1,
                        transition: 'all .15s ease',
                        '&:hover': {
                          boxShadow: 10,
                          transform: 'translateY(-1px)'}}}
                    >
                      <CardContent sx={{ pb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1}}
                        >
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 900,
                                lineHeight: 1.1}}
                            >
                              Hot
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary"
                              }}
                            >
                              Best for active acquisition
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                              gap: 0.75}}
                          >
                            <Chip
                              size="small"
                              label="Most popular"
                              color="warning"
                              sx={{ fontWeight: 900 }}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: 900,
                              letterSpacing: -0.5}}
                          >
                            {yearly
                              ? currencyWhole.format(
                                  Math.round((hotAnnual || 0) / 1200),
                                )
                              : currencyWhole.format(
                                  Math.round((hotMonthly || 0) / 100),
                                )}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary"
                            }}
                          >
                            per user / month{' '}
                            {yearly
                              ? '· billed annually'
                              : '· billed monthly'}
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          {[
                            'Everything in Regular plan',
                            'Buyer Match AI',
                            'Exclusive investor data : Fix & Flippers / private lenders / note holders & Landlords ',
                            'Unlimited skip tracing',
                            'Export + outreach-ready lists',
                          ].map((perk) => (
                            <Typography
                              key={perk}
                              variant="body2"
                              sx={{
                                display: 'flex',
                                gap: 1,
                                mb: 0.5}}
                            >
                              <span style={{ fontWeight: 900 }}>
                                ✓
                              </span>
                              <span>{perk}</span>
                            </Typography>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {isInTrial && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#ffebee',
                      border: '1px solid #ffcdd2'}}
                  >
                    <Typography
                      sx={{
                        color: '#d32f2f',
                        fontSize: 14,
                        fontWeight: 700}}
                    >
                      Upgrading will end your trial immediately and
                      billing will begin.
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid size={{xs: 12, md: 5}}>
                {selectedIsCurrentPlan ? (
                  <>
                    <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      mb: 2}}
                  >
                    <CardContent sx={{ bgcolor: '#f3f4f6' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          mb: 1}}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 900 }}>
                            You’re on this plan
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              mt: 0.25
                            }}>
                            {yearly ? 'Annual' : 'Monthly'}{' '}
                            {isHot ? 'Hot' : 'Regular'} ·{' '}
                            {/* {subscriptionStatus || 'active'} */}
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid size={12}>
                          <Grid
                            container
                            sx={{
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}>
                            <Typography sx={{
                              color: "text.secondary"
                            }}>
                              Last payment
                            </Typography>
                            <Typography sx={{ fontWeight: 900 }}>
                              {currentLastPaymentText}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid size={12}>
                          <Grid
                            container
                            sx={{
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}>
                            <Typography sx={{
                              color: "text.secondary"
                            }}>
                              {cancelAtPeriodEnd
                                ? 'Ends on'
                                : 'Next payment'}
                            </Typography>
                            <Typography sx={{ fontWeight: 900 }}>
                              {currentNextPaymentText}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid size={12}>
                          <Grid
                            container
                            sx={{
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}>
                            <Typography sx={{
                              color: "text.secondary"
                            }}>
                              Payment method
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75}}
                            >
                              <Typography sx={{ fontWeight: 900 }}>
                                {currentDefaultPm
                                  ? `${currentDefaultPm.brand || 'Card'} •••• ${
                                      currentDefaultPm.last4 || ''
                                    }`
                                  : '—'}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => setPmManageOpen(true)}
                                aria-label="Edit payment method"
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Typography
                        sx={{
                          color: "text.secondary",
                          mt: 1.5,
                          fontSize: 13
                        }}>
                        To make changes, select a different plan on
                        the left.
                      </Typography>

                      {/* Payment method editor is opened via pencil icon above */}
                    </CardContent>
                  </Card>
                  {pmManageOpen && (
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 2}}
                    >
                      <CardContent>
                        <Typography sx={{ fontWeight: 900, mb: 1 }}>
                          Payment methods
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mb: 2
                          }}>
                          Set your default card to control what we charge for
                          renewals.
                        </Typography>

                        {(paymentMethods || []).length > 0 ? (
                          <RadioGroup
                            value={selectedPm}
                            onChange={(e) => handlePickPm(e.target.value)}
                          >
                            {(paymentMethods || []).map((pm) => (
                              <FormControlLabel
                                key={pm.id}
                                value={pm.id}
                                disabled={isCardExpired(pm)}
                                control={<Radio />}
                                label={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 1,
                                      width: '100%',
                                      pr: 0.5}}
                                  >
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 700 }}
                                      >
                                        {pm.brand || 'Card'} ••••{' '}
                                        {pm.last4 || ''}{' '}
                                        <span
                                          style={{
                                            fontWeight: 500,
                                            color: 'rgba(0,0,0,.6)'}}
                                        >
                                          exp {pm.expMonth || ''}/
                                          {pm.expYear || ''}
                                        </span>
                                      </Typography>
                                        {isCardExpired(pm) && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 800,
                                              color: '#d32f2f'}}
                                          >
                                            Expired
                                          </Typography>
                                        )}
                                    </Box>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        flexShrink: 0}}
                                    >
                                      <Box
                                        sx={{
                                          minWidth: 118,
                                          display: 'flex',
                                          justifyContent: 'flex-end'}}
                                      >
                                        {isCardExpired(pm) ? (
                                          <Chip
                                            label={
                                              pm.isDefault
                                                ? 'Default (expired)'
                                                : 'Expired'
                                            }
                                            variant="outlined"
                                            sx={{
                                              height: 32,
                                              minWidth: 118,
                                              borderRadius: 999,
                                              fontWeight: 900,
                                              fontSize: 13,
                                              borderColor: '#fecaca',
                                              color: '#b91c1c',
                                              bgcolor: '#fff',
                                              '& .MuiChip-label': { px: 2 }}}
                                          />
                                        ) : pm.isDefault ? (
                                          <Chip
                                            label="Default"
                                            variant="outlined"
                                            sx={{
                                              height: 32,
                                              minWidth: 118,
                                              borderRadius: 999,
                                              fontWeight: 900,
                                              fontSize: 13,
                                              borderColor:
                                                'rgba(2,6,23,.25)',
                                              color: '#0b1020',
                                              bgcolor: '#fff',
                                              '& .MuiChip-label': {
                                                px: 2}}}
                                          />
                                        ) : (
                                          <Button
                                            size="small"
                                            variant="text"
                                            onClick={(ev) => {
                                              ev.preventDefault()
                                              ev.stopPropagation()
                                              handleMakeDefaultPm(pm.id)
                                            }}
                                            disabled={pmWorkingId === pm.id}
                                            sx={{
                                              minWidth: 118,
                                              textTransform: 'none',
                                              fontWeight: 900}}
                                          >
                                            Set default
                                          </Button>
                                        )}
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={(ev) => {
                                          ev.preventDefault()
                                          ev.stopPropagation()
                                          handleRemovePm(pm.id)
                                        }}
                                        disabled={pmWorkingId === pm.id}
                                        aria-label="Remove saved card"
                                      >
                                        <DeleteOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                }
                              />
                            ))}
                          </RadioGroup>
                        ) : (
                          <Typography
                            sx={{
                              color: "text.secondary",
                              mb: 2
                            }}>
                            No saved cards yet.
                          </Typography>
                        )}

                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mt: 1
                          }}>
                          Selecting a card won’t change renewals until you
                          confirm by setting it as the default.
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 1.25}}
                        >
                          <Button
                            variant="contained"
                            onClick={() => handleMakeDefaultPm(selectedPm)}
                            disabled={
                              !selectedPm ||
                              selectedPmExpired ||
                              selectedPm === defaultPmId ||
                              Boolean(pmWorkingId)
                            }
                          >
                            Use selected card for renewals
                          </Button>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          <Typography sx={{ fontWeight: 900, mb: 1 }}>
                            Add a new card
                          </Typography>
                          <Elements stripe={stripePromise}>
                            <PaymentForm
                              createToken={createAddCardToken}
                              setElement={setAddCardElement}
                              setResetToken={() => {}}
                            />
                          </Elements>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              mt: 1.5}}
                          >
                            <Button
                              variant="contained"
                              onClick={handleAddCard}
                              disabled={addingCard || !addCardToken}
                            >
                              {addingCard ? 'Adding…' : 'Add card'}
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                  </>
                ) : (
                  <>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 2}}
                    >
                      <CardContent sx={{ bgcolor: '#f3f4f6' }}>
                        <Typography sx={{ fontWeight: 900, mb: 1 }}>
                          {yearly ? 'Annual' : 'Monthly'}{' '}
                          {isHot ? 'Hot' : 'Regular'} plan
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid size={12}>
                            <Grid
                              container
                              sx={{
                                justifyContent: "space-between",
                                alignItems: "center"
                              }}>
                              <Typography sx={{
                                color: "text.secondary"
                              }}>
                                Subtotal
                              </Typography>
                              <Typography sx={{ fontWeight: 800 }}>
                                {yearly && selectedAnnual
                                  ? currencyCents.format(
                                      (selectedAnnual || 0) / 100,
                                    )
                                  : selectedMonthly
                                    ? currencyCents.format(
                                        (selectedMonthly || 0) / 100,
                                      )
                                    : currencyCents.format(0)}
                              </Typography>
                            </Grid>
                          </Grid>

                          {discountAmountCents > 0 &&
                            grossProratedCents !== null && (
                              <Grid size={12}>
                                <Grid
                                  container
                                  sx={{
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                  }}>
                                  <Typography sx={{
                                    color: "text.secondary"
                                  }}>
                                    Prorated amount due today
                                  </Typography>
                                  <Typography sx={{ fontWeight: 700 }}>
                                    {currencyCents.format(
                                      (grossProratedCents || 0) / 100,
                                    )}
                                  </Typography>
                                </Grid>
                              </Grid>
                            )}

                          {discountAmountCents > 0 && (
                            <Grid size={12}>
                              <Grid
                                container
                                sx={{
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}>
                                <Typography sx={{ color: '#2e7d32' }}>
                                  Discount
                                </Typography>
                                <Typography
                                  sx={{
                                    color: '#2e7d32',
                                    fontWeight: 800}}
                                >
                                  -
                                  {currencyCents.format(
                                    (discountAmountCents || 0) / 100,
                                  )}
                                </Typography>
                              </Grid>
                            </Grid>
                          )}

                          <Grid size={12}>
                            <Grid
                              container
                              sx={{
                                justifyContent: "space-between",
                                alignItems: "center"
                              }}>
                              <Typography
                                sx={{
                                  fontWeight:
                                    discountAmountCents > 0
                                      ? 800
                                      : 600}}
                              >
                                {discountAmountCents > 0
                                  ? 'Amount due today'
                                  : 'Prorated amount due today'}
                              </Typography>
                              {previewLoading ? (
                                <Typography
                                  sx={{ fontStyle: 'italic' }}
                                >
                                  Calculating…
                                </Typography>
                              ) : (
                                <Typography
                                  sx={{
                                    fontWeight:
                                      discountAmountCents > 0
                                        ? 900
                                        : 700}}
                                >
                                  {currencyCents.format(
                                    (amountDueDisplayCents || 0) /
                                      100,
                                  )}
                                </Typography>
                              )}
                            </Grid>
                          </Grid>

                          <Grid size={12}>
                            <Typography sx={{ fontWeight: 900 }}>
                              {nextPaymentDateText
                                ? `Next payment on ${nextPaymentDateText}.`
                                : `Next payment due a ${
                                    yearly ? 'year' : 'month'
                                  } from now.`}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>

                    <TextField
                      label="Coupon code (optional)"
                      value={couponCode}
                      onChange={(e) => {
                        const newValue = e.target.value.toUpperCase()
                        setCouponCode(newValue)
                        setCouponError('')
                        if (
                          newValue.trim() !== appliedCouponCode.trim()
                        ) {
                          setAppliedCouponCode('')
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && couponCode.trim()) {
                          handleApplyCoupon()
                        }
                      }}
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={!!couponError}
                      helperText={
                        couponError ||
                        (appliedCouponCode && discountAmountCents > 0
                          ? `✓ Coupon applied! Discount: ${currencyCents.format(
                              (discountAmountCents || 0) / 100,
                            )}`
                          : appliedCouponCode &&
                              discountAmountCents === 0
                            ? 'Coupon applied (no discount)'
                            : '')
                      }
                      sx={{ mb: 2 }}
                      slotProps={{
                        input: {
                          style: { textTransform: 'uppercase' },
                          endAdornment: (
                            <InputAdornment position="end">
                              {appliedCouponCode &&
                              appliedCouponCode.trim() ===
                                couponCode.trim() ? (
                                <Box
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4}}
                                >
                                  <CheckCircleIcon
                                    style={{
                                      color: '#2e7d32',
                                      fontSize: 20,
                                      marginRight: 4}}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={handleClearCoupon}
                                    style={{ padding: 4 }}
                                    aria-label="Remove coupon"
                                  >
                                    <CancelIcon
                                      style={{ fontSize: 18 }}
                                    />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={handleApplyCoupon}
                                  disabled={
                                    !couponCode.trim() ||
                                    couponValidating ||
                                    (appliedCouponCode &&
                                      appliedCouponCode.trim() ===
                                        couponCode.trim())
                                  }
                                  style={{
                                    minWidth: 70,
                                    height: 32,
                                    fontSize: 12,
                                    textTransform: 'none'}}
                                >
                                  {couponValidating
                                    ? 'Applying...'
                                    : 'Apply'}
                                </Button>
                              )}
                            </InputAdornment>
                          )}
                      }}
                    />

                    {hasExistingCard ? (
                      <>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={useExisting}
                              onChange={handleExistingToggle}
                            />
                          }
                          label="Use a saved card"
                          sx={{ mb: 1 }}
                        />
                        {useExisting && (
                          <RadioGroup
                            value={selectedPm}
                            onChange={(e) =>
                              handlePickPm(e.target.value)
                            }
                          >
                            {(paymentMethods || []).map((pm) => (
                              <FormControlLabel
                                key={pm.id}
                                value={pm.id}
                                control={<Radio />}
                                label={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 1,
                                      width: '100%',
                                      pr: 0.5}}
                                  >
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 700 }}
                                      >
                                        {pm.brand || 'Card'} ••••{' '}
                                        {pm.last4 || ''}{' '}
                                        <span
                                          style={{
                                            fontWeight: 500,
                                            color: 'rgba(0,0,0,.6)'}}
                                        >
                                          exp {pm.expMonth || ''}/
                                          {pm.expYear || ''}
                                        </span>
                                      </Typography>
                                    </Box>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        flexShrink: 0}}
                                    >
                                      <Box sx={{ minWidth: 118, display: 'flex', justifyContent: 'flex-end' }}>
                                        {pm.isDefault ? (
                                          <Chip
                                            label="Default"
                                            variant="outlined"
                                            sx={{
                                              height: 32,
                                              minWidth: 118,
                                              borderRadius: 999,
                                              fontWeight: 900,
                                              fontSize: 13,
                                              borderColor: 'rgba(2,6,23,.25)',
                                              color: '#0b1020',
                                              bgcolor: '#fff',
                                              '& .MuiChip-label': {
                                                px: 2}}}
                                          />
                                        ) : (
                                          <Button
                                            size="small"
                                            variant="text"
                                            onClick={(ev) => {
                                              ev.preventDefault()
                                              ev.stopPropagation()
                                              handleMakeDefaultPm(pm.id)
                                            }}
                                            disabled={pmWorkingId === pm.id}
                                            sx={{
                                              minWidth: 118,
                                              textTransform: 'none',
                                              fontWeight: 900}}
                                          >
                                            Set default
                                          </Button>
                                        )}
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={(ev) => {
                                          ev.preventDefault()
                                          ev.stopPropagation()
                                          handleRemovePm(pm.id)
                                        }}
                                        disabled={pmWorkingId === pm.id}
                                        aria-label="Remove saved card"
                                      >
                                        <DeleteOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                }
                              />
                            ))}
                          </RadioGroup>
                        )}
                      </>
                    ) : (
                      <Typography
                        sx={{
                          color: "text.secondary",
                          mb: 1
                        }}>
                        No saved cards on file. Please add a new card
                        below.
                      </Typography>
                    )}

                    {!useExisting && (
                      <Box sx={{ mb: 1 }}>
                        <Elements stripe={stripePromise}>
                          <PaymentForm
                            createToken={createToken}
                            setElement={setElement}
                            setResetToken={() => {}}
                          />
                        </Elements>
                        <Typography>
                          <div
                            id="card-errors"
                            role="alert"
                            style={{
                              color: 'red',
                              fontSize: 13,
                              marginTop: 3,
                              fontWeight: 400}}
                          ></div>
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                {!selectedIsCurrentPlan && (
                  <>
                    {Boolean(errorText) && (
                      <Typography
                        role="alert"
                        sx={{ color: '#d32f2f', fontSize: 13, mt: 1 }}
                      >
                        {errorText}
                      </Typography>
                    )}

                    <Typography
                      sx={{
                        color: "text.secondary",
                        mt: 2,
                        fontSize: 13
                      }}>
                      By continuing, you agree to our{' '}
                      <a
                        href="/Terms"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Terms & Conditions
                      </a>{' '}
                      and our{' '}
                      <a
                        href="/Privacy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Privacy Policy
                      </a>
                      .
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {success ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
          >
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                selectedIsCurrentPlan ||
                !priceId ||
                (!useExisting && !stripeToken) ||
                (useExisting &&
                  hasExistingCard &&
                  (!selectedPm || selectedPmExpired))
              }
            >
              {selectedIsCurrentPlan ? 'Current plan' : 'Submit'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default UnifiedSubscribeDialog
